import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { postNamedCommand, postNamedQuery } from '../service'
import { useAppContext } from '../redux/slices/appContext'
import { QUERY_KEYS } from './queryKeys'

/**
 * Read: list registrations linked to a household, with their owner-group + weight.
 *
 * Returns one row per (account, registration, owner-assignment) tuple.
 * Multi-owner registrations produce multiple rows; registrations without an
 * owner produce a row with null ownerGroupId/ownerGroupName/weight.
 *
 * Per-household authorization is enforced server-side; cross-firm or
 * cross-household requests yield an empty array.
 */
export const useListHouseholdRegistrations = (clientId, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = options
  return useQuery({
    queryKey: [QUERY_KEYS.listHouseholdRegistrations, userId, clientId],
    enabled: enabled && Number.isFinite(clientId) && clientId > 0,
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'list-household-registrations', { clientId })
      return data
    }
  })
}

function useInvalidateHousehold () {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return (clientId) => {
    queryClient
      .invalidateQueries({ queryKey: [QUERY_KEYS.listHouseholdRegistrations, userId, clientId], refetchType: 'all' })
      .catch(console.error)
    queryClient
      .invalidateQueries({ queryKey: [QUERY_KEYS.searchGroups, userId], refetchType: 'all' })
      .catch(console.error)
  }
}

/**
 * Write: assign a registration to an owner group with a weight.
 *
 * 409 path: returns Boom.conflict if the registration is already assigned to
 * the given owner group; the response payload includes the existing
 * groupMemberId so the UI can switch to "edit weight" instead.
 */
export const useAssignRegistrationOwnerGroupMutation = () => {
  const invalidate = useInvalidateHousehold()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'assign-registration-owner-group', command)
      return data
    },
    onSuccess: (_data, command) => {
      invalidate(command.clientId)
    }
  })
}

/**
 * Write: modify the weight on an existing registration -> owner-group assignment.
 *
 * 409 path: server returns Boom.conflict when the underlying row is missing
 * (was modified or removed by another user). UI should show a banner +
 * trigger refetch.
 */
export const useModifyRegistrationOwnerWeightMutation = () => {
  const invalidate = useInvalidateHousehold()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'modify-registration-owner-weight', command)
      return data
    },
    onSuccess: (_data, command) => {
      invalidate(command.clientId)
    }
  })
}

/**
 * Write: remove a registration -> owner-group assignment.
 *
 * 409 path: same as modify-weight (row already removed).
 */
export const useRemoveRegistrationOwnerAssignmentMutation = () => {
  const invalidate = useInvalidateHousehold()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'remove-registration-owner-assignment', command)
      return data
    },
    onSuccess: (_data, command) => {
      invalidate(command.clientId)
    }
  })
}

/**
 * Write: create a new owner group (levels.groups, levelTypeId=207) for the firm.
 *
 * 409 path: if a group with the same name already exists for this firm at
 * levelTypeId=207, the response payload includes the existing groupId so
 * the UI can adopt it instead of creating a duplicate.
 *
 * Note: this does NOT also assign the new group to a registration. The UI
 * orchestrates create-then-assign as two separate calls (per SMT-994 ISSUE 8).
 */
export const useCreateOwnerGroupMutation = () => {
  const invalidate = useInvalidateHousehold()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'create-owner-group', command)
      return data
    },
    onSuccess: (_data, command) => {
      invalidate(command.clientId)
    }
  })
}
